import { Link, Outlet } from 'react-router-dom';
import { Settings, FolderOpen, Key, ExternalLink } from 'lucide-react';
import { SignedIn, SignedOut, UserButton } from './auth/DevModeAuth';
import { Button } from '@/components/ui/button';

const HOMEPAGE_URL = import.meta.env.VITE_HOMEPAGE_URL || 'https://keyenv.dev';

export function Layout() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <a href={HOMEPAGE_URL} className="flex items-center gap-2">
                <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                  <Key className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold text-foreground">KeyEnv</span>
              </a>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
                <SignedIn>
                  <NavLink to="/projects" icon={<FolderOpen className="w-4 h-4" />}>
                    Projects
                  </NavLink>
                  <NavLink to="/settings" icon={<Settings className="w-4 h-4" />}>
                    Settings
                  </NavLink>
                </SignedIn>
                <SignedOut>
                  <a
                    href={HOMEPAGE_URL}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Homepage
                  </a>
                </SignedOut>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SignedIn>
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
              <SignedOut>
                <Button variant="ghost" asChild>
                  <Link to="/login">Sign In</Link>
                </Button>
                <Button asChild>
                  <Link to="/signup">Sign Up</Link>
                </Button>
              </SignedOut>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 bg-primary rounded-md flex items-center justify-center">
                <Key className="h-3.5 w-3.5 text-primary-foreground" />
              </div>
              <span className="text-sm font-medium text-foreground">KeyEnv</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Secure environment variable management
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <a href={HOMEPAGE_URL} className="hover:text-foreground transition-colors">
                Home
              </a>
              <a href={`${HOMEPAGE_URL}/docs`} className="hover:text-foreground transition-colors">
                Docs
              </a>
              <a href={`${HOMEPAGE_URL}/privacy`} className="hover:text-foreground transition-colors">
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

function NavLink({ to, children, icon }: NavLinkProps) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-accent transition-colors"
    >
      {icon}
      {children}
    </Link>
  );
}
